//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useRef, useContext, Fragment, useCallback } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Tooltip, Link } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import SearchIcon from '@material-ui/icons/Search';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useLocation, useHistory } from 'react-router-dom';
// import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { PATH_JOB_CENTER, PATH_SUB_PAGE_EMP } from '../../system/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BRAND_PAGE_TAB_ID_JOBS, LABEL_COMPANY_SIZE, LABEL_INDUSTRY, LABEL_LOCATION, VIEW_ACTIVE_JOBS, VISIT_BRAND_PAGE } from '../../common/constants';
import { getCityStateCountryText, getCompanySizeText } from '../../common/textHelper';
import { getBrandPageUrlPathByEmployerId } from '../../common/linkHelper';
import { isQueryEmpty } from '../../common/queryHelper';
import { ELASTICSEARCH_PAGE_SIZE } from '../../common/elasticSearchHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { searchEmployers } from '../../common/services/empSearchServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdLink from '../../common/elemental/AfitdLink';
import BrandPageDialog from '../../empBrandPage/subComponents/BrandPageDialog';
import AfitdPagination from '../../common/elemental/AfitdPagination';
import AfitdAdContainer from '../../common/elemental/AfitdAdContainer';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import SearchInput from '../../dashboardMyJobs/subComponents/SearchInput';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import { PLACEHOLDER_SEARCH_BY_COMPANY_NAME } from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            background: 'white',
            borderRadius: 10,
            padding: 24,
            marginBottom: 24,
            // height: '800px',
            // height: '1600px',
            [theme.breakpoints.down('md')]: { marginTop: 16 },
            // [theme.breakpoints.up('lg')]: { height: '100vh' },
        },
        headerTabs: {
            background: '#FAFAFC',
        },
        closeIcon: { fontSize: 18 },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '32px',
            // marginBottom: '32px',
            alignItems: 'center',
            borderBottom: '1px solid #F0F2F6',
            paddingBottom: 10,
            [theme.breakpoints.down('sm')]: { flexDirection: 'column', alignItems: 'flex-start' },
        },
        employerCount: {
            fontSize: '20px',
            fontWeight: 'bold',
        },
        selectSort: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: { paddingTop: 8 },
        },
        sortLabel: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#1D8A8B',
            marginRight: '10px',
        },
        sortBy: {
            background: 'white',
            width: '200px',
            '&:nth-child(1)': {
                background: 'red',
            },
        },
        companyProfilePic: {
            width: 90,
            height: 90,
            marginRight: '24px',
            border: '1px solid #D5D7DD',
            background: 'rgba(252, 130, 31, 0.1)',
            color: 'rgb(252,130,31)',
            fontSize: '35px',
            borderRadius: 10,
        },
        companyNameLink: {
            textDecoration: 'underline',
            // color: '#111E14',
        },
        employeTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            lineHeight: '28px',
            marginTop: '0px  ',
            marginBottom: '0px',
        },
        employeAddress: {
            fontSize: '16px',
            lineHeight: '19px',
            marginTop: '0px  ',
            marginBottom: '7px',
        },
        verified: {
            color: '#1D8A8B',
            position: 'relative',
            top: '6px',
        },
        services: {
            fontSize: '14px',
            fontWeight: 'bold',
            marginRight: '16px',
        },
        industryIcons: {
            color: '#9B9E9C',
            position: 'relative',
            top: '3px',
            marginRight: '6px',
            fontSize: '16px',
        },
        bodyGrid: {
            borderBottom: '1px solid #F0F2F6',
            paddingTop: 24,
            paddingBottom: 24,
        },
        jobCounter: {
            color: '#FC821F',
            fontWeight: 'bold',
        },
        activeJobCountLink: {
            textDecoration: 'underline',
            fontWeight: 'bold',
        },
        resultListContainer: {
            flex: 1,
            height: 375,
            overflowY: 'scroll',
            overflowX: 'hidden',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
            [theme.breakpoints.down('md')]: { height: 430 },
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: 10,
        },
        noResultIcon: {
            fontSize: 280,
            color: '#808080',
        },
        noResultContainer: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
        },
        noResultText: {
            fontSize: 20,
            fontWeight: 'bold',
        },
        noResultTryText: {
            fontSize: 16,
            color: '#6F6D7A',
        },
        noResultTextContainer: {
            textAlign: 'center',
            marginTop: '-20px',
        },
        adContainer: {
            marginTop: '40px',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        empProfileList = [],
        // setEmpProfileList,
        handleRunSearch,
        handleClearFilters,
        brandPageDialogInfo,
        setBrandPageDialogInfo,
        handleCloseBrandPageDialog,
        sortBy,
        setSortBy,
    } = params;

    const brandPageDialogOpen = _get(brandPageDialogInfo, 'open');
    const brandPageEmployerId = _get(brandPageDialogInfo, 'employerId');

    const defns = {
        searchInput: {
            noClearFilters: true,
            placeholder: PLACEHOLDER_SEARCH_BY_COMPANY_NAME,
            onSearch: handleRunSearch,
            showClearFilters: true,
            onClearFilters: handleClearFilters,
        },
        sortBy: {
            name: 'sort',
            placeholder: 'Sort',
            options: [LABEL_COMPANY_SIZE, LABEL_LOCATION, LABEL_INDUSTRY],
            value: sortBy,
            onChange: (evt, option) => {
                setSortBy(option);
            },
        },
        employerList: {
            list: empProfileList,
            // list: _slice(empProfileList, start, end),
            setBrandPageDialogInfo,
        },
        brandPageDialog: {
            open: brandPageDialogOpen,
            onClose: handleCloseBrandPageDialog,
            employerId: brandPageEmployerId,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

const EmployerList = props => {
    // const classes = useStyles()();
    const { list = [], setBrandPageDialogInfo } = props;
    // const infoData = _get(data, 'info')

    // const defns = {};

    // // Automatically inject className for matching keys
    // setClassNameInDefns(defns, classes);

    return _map(list, item => <EmployerItem data={item} setBrandPageDialogInfo={setBrandPageDialogInfo} />);
};

const EmployerItem = props => {
    const classes = useStyles()();
    const theme = useTheme();
    const history = useHistory();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));
    const { data, setBrandPageDialogInfo } = props;

    const searchResultEmployerId = _get(data, 'employerId');

    const companyName = _get(data, 'info.companyName');
    const isEmpBadge = _get(data, 'info.isEmpBadge');
    const isTier3 = !!isEmpBadge;
    const profilePicUrl = _get(data, 'brand.profilePicUrl');
    const city = _get(data, 'info.city');
    const state = _get(data, 'info.state');
    const country = _get(data, 'info.country');
    const companySize = _get(data, 'info.companySize');
    const industry = _get(data, 'info.industry');

    const activeJobsCount = _get(data, 'activeJobsCount', 0);

    const companySizeText = getCompanySizeText(companySize, null);

    const handleClickCompanyLogo = evt => {
        evt.stopPropagation();
        setBrandPageDialogInfo({ open: true, employerId: searchResultEmployerId });
    };

    const handleClickActiveJobCount = evt => {
        evt.stopPropagation();
        history.push(`${PATH_JOB_CENTER}/${PATH_SUB_PAGE_EMP}/${searchResultEmployerId}`);
    };

    const defns = {
        bodyGrid: { container: true },
        logoGrid: {
            item: true,
            // style: {
            //     marginRight: '30px',
            // },
        },
        companyDetailsGrid: { item: true },
        employerLogoLink: {
            isBrandPage: false, // My Jobs will never be inside a Brand Page so the logo should always be a link
            onClick: handleClickCompanyLogo,
            profilePicUrl: profilePicUrl,
            brandEmployerId: searchResultEmployerId,
        },
        companyNameLink: {
            color: 'secondary',
            title: VISIT_BRAND_PAGE,
            onClick: mdMatchesUp ? undefined : handleClickCompanyLogo,
            href: mdMatchesUp ? getBrandPageUrlPathByEmployerId(searchResultEmployerId) : undefined,
            target: mdMatchesUp ? '_blank' : undefined,
        },
        employeTitle: {
            color: 'secondary',
        },
        activeJobCountLink: {
            title: VIEW_ACTIVE_JOBS,
            href: isTier3 ? `${getBrandPageUrlPathByEmployerId(searchResultEmployerId)}/${BRAND_PAGE_TAB_ID_JOBS}` : undefined,
            target: '_blank',
            onClick: isTier3 ? undefined : handleClickActiveJobCount,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return (
        <Grid {...defns.bodyGrid}>
            <Grid {...defns.logoGrid}>
                <EmployerLogoLink {...defns.employerLogoLink} />
            </Grid>
            <Grid {...defns.companyDetailsGrid}>
                <AfitdLink {...defns.companyNameLink}>
                    <AfitdTypography {...defns.employeTitle}>
                        {companyName} {isEmpBadge && <VerifiedUserIcon {...defns.verified} />}
                    </AfitdTypography>
                </AfitdLink>
                <p {...defns.employeAddress}>{getCityStateCountryText(city, state, country)}</p>
                <div>
                    {companySizeText && <span {...defns.services}>{companySizeText}</span>}
                    {industry && (
                        <span {...defns.services}>
                            <BusinessIcon {...defns.industryIcons} />
                            {industry}
                        </span>
                    )}
                    <span {...defns.services}>
                        Active Jobs{' '}
                        <span {...defns.jobCounter}>
                            {activeJobsCount > 0 && <AfitdLink {...defns.activeJobCountLink}>({activeJobsCount})</AfitdLink>}
                            {activeJobsCount === 0 && <Fragment>({activeJobsCount})</Fragment>}
                        </span>
                    </span>
                </div>
            </Grid>
        </Grid>
    );
};

const EmployerLogoLink = props => {
    const { isBrandPage, brandEmployerId, onClick, profilePicUrl } = props;
    const classes = useStyles({ isBrandPage })();
    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    const defns = {
        logoLinkContainer: {
            onClick: isBrandPage ? undefined : onClick,
        },
        logoLink: {
            href: getBrandPageUrlPathByEmployerId(brandEmployerId),
            target: '_blank',
        },
        companyProfilePic: {
            variant: 'rounded',
            src: profilePicUrl,
        },
        tooltip: {
            title: VISIT_BRAND_PAGE,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return isBrandPage ? (
        <div {...defns.logoContainer}>
            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
        </div>
    ) : (
        <div {...defns.logoContainer}>
            <Tooltip {...defns.tooltip}>
                <div>
                    {mdMatchesUp ? (
                        <Link {...defns.logoLink}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </Link>
                    ) : (
                        <div {...defns.logoLinkContainer}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Listing = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const location = useLocation();
    const appContext = useContext(AppContext);
    const { esToken, setEsToken } = appContext;

    const { queryInitState, query, setQuery /* searchLocation, filters, haveActiveJobs setSnackbarMessage, setSnackbarErrorMessage*/ } = useContext(LayoutContext);

    // const theme = useTheme();
    // const mdMatches = useMediaQuery(theme.breakpoints.down('md'));

    const topNavEmpSearchString = _get(location, 'state.empSearchString');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [empProfileList, setEmpProfileList] = useState();
    const [searchResultTotal, setSearchResultTotal] = useState(0);
    const [brandPageDialogInfo, setBrandPageDialogInfo] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    // Sorting is removed from UI per requirement -el 20211210
    // Keep the state in case we need sorting in future -el 20211210
    const [sortBy, setSortBy] = useState(null);

    const searchInputRef = useRef();
    const myRef = useRef(null);
    const lastQuery = useRef();
    const lastQueryPageNumber = useRef();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    const { showAds, setShowAds, advertisements } = props;

    //---------------------------------------------------------------------
    // ElasticSearch
    //---------------------------------------------------------------------
    const runSearch = useCallback(
        async ({ page = 1 } = {}) => {
            let searchResult = [];

            if (!isQueryEmpty(query)) {
                searchResult = await searchEmployers({
                    queryObject: query,
                    from: page,
                    esToken,
                    setEsToken,
                });
            }

            setSearchResultTotal(_get(searchResult, 'total', 0));
            const employerList = _get(searchResult, 'list', []);
            setEmpProfileList(employerList);

            //TODO Remove console log
            console.log(employerList);
            // myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        [query, esToken, setEsToken],
    );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on load, set focus on Search Box
    useEffect(() => {
        const searchInputEl = _get(searchInputRef, 'current');
        if (searchInputEl) {
            searchInputEl.focus();
        }
    }, [searchInputRef]);

    // top nav search string has changed
    useEffect(() => {
        if (topNavEmpSearchString) {
            setQuery(prev => ({ ...prev, searchText: topNavEmpSearchString }));
        } else {
        }
    }, [topNavEmpSearchString, setQuery]);

    // run search when query has changed
    useEffect(() => {
        if (query && currentPageNumber) {
            const sameQuery = _isEqual(lastQuery.current, query);
            const samePageNumber = lastQueryPageNumber.current === currentPageNumber;

            // if query is same as last, do not search
            if (lastQuery && lastQueryPageNumber && (!sameQuery || !samePageNumber)) {
                runSearch({ page: currentPageNumber });

                // remember the query and the page number
                lastQuery.current = _cloneDeep(query);
                lastQueryPageNumber.current = currentPageNumber;
            }
        }
    }, [query, currentPageNumber, runSearch, lastQuery, lastQueryPageNumber]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const employerCountText = `${searchResultTotal} Employer${searchResultTotal > 1 || searchResultTotal === 0 ? 's' : ''} Found`;
    const brandPageDialogOpen = _get(brandPageDialogInfo, 'open');

    const paginationNumber = searchResultTotal ? Math.ceil(searchResultTotal / ELASTICSEARCH_PAGE_SIZE) : 1;
    const noSearchIcon = empProfileList ? searchResultTotal > 0 : false;

    const searchTextInQuery = _get(query, 'searchText');

    //---------------------------------------------------------------------
    // useCallback / Event handler
    //---------------------------------------------------------------------
    const handleRunSearch = useCallback(
        searchText => {
            // if searching with new keywords, reset current page number to page 1
            if (searchText !== searchTextInQuery) setCurrentPageNumber(1);
            setQuery(prev => ({ ...prev, searchText }));
            setShowAds(false);
        },
        [searchTextInQuery, setQuery, setShowAds],
    );

    const handleClearFilters = () => {
        setQuery(queryInitState);
    };

    const handleCloseBrandPageDialog = () => {
        setBrandPageDialogInfo(null);
    };

    const handleChangePage = (event, value) => {
        setCurrentPageNumber(value);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        empProfileList,
        setEmpProfileList,
        handleRunSearch,
        handleClearFilters,
        brandPageDialogInfo,
        setBrandPageDialogInfo,
        handleCloseBrandPageDialog,
        sortBy,
        setSortBy,
    });
    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div>
                <SearchInput {...defns.searchInput} />
            </div>
            {showAds ? (
                <div {...defns.adContainer}>
                    <AfitdAdContainer noPadding={true} data={advertisements} />
                </div>
            ) : (
                <>
                    <div {...defns.header}>
                        <div {...defns.employerCount}>{employerCountText}</div>
                    </div>

                    {noSearchIcon ? (
                        <div {...defns.resultListContainer}>
                            <div ref={myRef} />

                            <div>
                                <EmployerList {...defns.employerList} />
                            </div>

                            <div
                                {...defns.paginationContainer}
                                onClick={() => {
                                    setTimeout(() => {
                                        myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                }}
                            >
                                <AfitdPagination count={paginationNumber} color="secondary" onChange={handleChangePage} />
                            </div>
                        </div>
                    ) : (
                        <div {...defns.noResultContainer}>
                            <SearchIcon {...defns.noResultIcon} />
                            <div {...defns.noResultTextContainer}>
                                <p {...defns.noResultText}>There are no employers that match the criteria</p>
                                <p>Please enter more details and try again</p>
                            </div>
                        </div>
                    )}
                </>
            )}

            {brandPageDialogOpen && <BrandPageDialog {...defns.brandPageDialog} />}
        </div>
    );
};

export default Listing;
